import React, { useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import useI18n from '@/hooks/useI18n';
import DragDropComponent from './DragDropComponent';
import { FormDataForCreateCampaign } from '@/types/interfaces';
import { Button } from '../Button';
import { useUploadBrandImageMutation } from '@/hooks/queries/useLinkQueries';
// import CustomDatePicker from '@/components/dashboard/date-picker/CustomDatePicker';
import Checkbox from '../Checkbox';
import ToolTip from '../ToolTip';
import Input from '../Input';
// import dayjs from 'dayjs';
// import utc from 'dayjs/plugin/utc';
import { InfoIcon } from '@/svg';
// dayjs.extend(utc);
import { DatePicker, ConfigProvider } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import enUS from 'antd/locale/en_US';
import { useContext, useEffect } from 'react';
import { UserContext } from '@/context/UserContext';
import { useNavigate, useLocation } from 'react-router-dom';
import ActiveCampaignRunningModal from '@/components/Modal/ActiveCampaignRunningModal';

interface CreateCampaignStep1Props {
  formData: FormDataForCreateCampaign;
  handleChange: (
    newValues: Partial<FormDataForCreateCampaign>,
    options?: { skipDirty?: boolean },
  ) => void;
  imagePreview: string | null;
  setImagePreview: React.Dispatch<React.SetStateAction<string | null>>;
  onNext: () => void;
}

const CreateCampaignStep1: React.FC<CreateCampaignStep1Props> = ({
  formData,
  handleChange,
  imagePreview,
  setImagePreview,
  onNext,
}) => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [previewFileName, setPreviewFileName] = useState<string | null>(
    formData.brandLogo,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isBalanceExhausted, setIsBalanceExhausted] = useState<boolean>(
    formData.runCampaignTillMoneyEnd,
  );
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormDataForCreateCampaign, string>>
  >({});
  const [isImageValid, setIsImageValid] = useState<boolean>(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const originalStartDateRef = useRef<string | undefined>(formData.startDate);
  const originalEndDateRef = useRef<string | undefined>(formData.endDate);
  const [showActiveCampaignModal, setShowActiveCampaignModal] = useState(false);
  const { t } = useI18n();
  const { mutateAsync: uploadBrandImage } = useUploadBrandImageMutation();
  const userContext = useContext(UserContext);

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const navigate = useNavigate();
  const location = useLocation();
  const isEditCampaign = location.pathname.startsWith('/edit-campaign/');

  const { user, campaignStats } = userContext;
  const profileBrandLogo = user?.brand?.brandLogo || null;
  const hasBrandLogo = Boolean(profileBrandLogo);

  useEffect(() => {
    if (!formData.brandName && user?.brand?.name) {
      handleChange({ brandName: user.brand.name }, { skipDirty: true });
    }
  }, [user?.brand?.name]);
  // useEffect(() => {
  //   if (campaignStats?.live > 0 && isEditCampaign) {
  //     setShowActiveCampaignModal(true);
  //   }
  // }, [campaignStats, isEditCampaign]);
  useEffect(() => {
    if (!imagePreview && profileBrandLogo) {
      setImagePreview(profileBrandLogo);
      handleChange({ brandLogo: profileBrandLogo }, { skipDirty: true });
    }
  }, [profileBrandLogo]);
  const formatDateTimeLocal = (isoString?: string) => {
    if (!isoString) return '';
    const d = new Date(isoString);

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };
  const startDateRef = useRef<HTMLInputElement | null>(null);
  const endDateRef = useRef<HTMLInputElement | null>(null);
  const isToday = (value?: Dayjs | null) =>
    value ? value.isSame(dayjs(), 'day') : false;

  const toISO = (value: Dayjs | null) =>
    value ? value.toDate().toISOString() : undefined;
  const toLocalDateTimeString = (value: Dayjs | null) =>
    value ? value.format('YYYY-MM-DDTHH:mm:ss') : undefined;

  const [isStartFocused, setIsStartFocused] = useState(false);
  const [isEndFocused, setIsEndFocused] = useState(false);

  const validationSchema = useMemo(() => {
    return Yup.object({
      brandLogo: Yup.string()
        .required(String(t('common.mandatoryField')))
        .test('is-valid-image', String(t('common.imageNotSquareError')), () => {
          return isImageValid;
        }),
      brandName: Yup.string().required(String(t('common.mandatoryField'))),
      brandDescription: Yup.string()
        .required(String(t('common.mandatoryField')))
        .max(300, String(t('common.maxCharacters'))),
      startDate: Yup.string()
        .required(String(t('common.mandatoryField')))
        .test(
          'start-date-not-in-past',
          'Start date cannot be in the past',
          function (value) {
            if (!value) return false;

            const selected = dayjs(value);
            if (!selected.isValid()) return false;

            const original = originalStartDateRef.current;
            if (original && selected.isSame(dayjs(original), 'minute')) {
              return true;
            }
            const bufferEndTime = selected.add(15, 'minute');
            return dayjs().isBefore(bufferEndTime);
          },
        ),
      ...(!isBalanceExhausted && {
        endDate: Yup.string()
          .transform((value) => {
            if (!value || value === '') return undefined;
            return value;
          })
          .required(String(t('common.mandatoryField')))
          .test('is-date', String(t('common.mandatoryField')), (value) => {
            if (!value) return false;
            const date = new Date(value);
            return !isNaN(date.getTime());
          })
          .test(
            'end-date-min-1-month',
            'End date must be at least 1 month after the start date',
            function (value, context) {
              if (!value || !context.parent.startDate) return true;

              const originalStart = originalStartDateRef.current;
              const originalEnd = originalEndDateRef.current;

              const currentStart = dayjs(context.parent.startDate).startOf(
                'minute',
              );
              const currentEnd = dayjs(value).startOf('minute');
              if (
                originalStart &&
                originalEnd &&
                currentStart.isSame(dayjs(originalStart), 'minute') &&
                currentEnd.isSame(dayjs(originalEnd), 'minute')
              ) {
                return true;
              }
              const minEndDate = currentStart.add(1, 'month');
              return currentEnd.isAfter(minEndDate);
            },
          ),
      }),
    });
  }, [isBalanceExhausted, t, isImageValid]);
  const formatDateTimeText = (isoString?: string) => {
    if (!isoString) return '';
    const d = new Date(isoString);

    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');

    return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsBalanceExhausted(checked);

    const updates: Partial<FormDataForCreateCampaign> = {
      runCampaignTillMoneyEnd: checked,
    };
    if (checked) {
      updates.endDate = undefined;
    }
    handleChange(updates);
  };
  const handleChangeWithValidation = (
    field: keyof FormDataForCreateCampaign,
    value: string | boolean | Date | undefined,
  ) => {
    if (isBalanceExhausted && field === 'endDate') {
      return;
    }

    // Convert Date objects to ISO strings for formData
    const formattedValue = value instanceof Date ? value.toISOString() : value;
    const updates: Partial<FormDataForCreateCampaign> = {
      [field]: formattedValue,
    };
    if (field === 'brandDescription') {
      const text = (formattedValue as string) || '';

      if (text.length > 300) {
        setErrors((prev) => ({
          ...prev,
          brandDescription: String(t('common.maxCharacters')),
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          brandDescription: undefined,
        }));
      }
    } else {
      // ✅ Clear error for other fields immediately
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
    if (
      field === 'startDate' &&
      formattedValue &&
      typeof formattedValue !== 'boolean'
    ) {
      const startDate = new Date(formattedValue);
      const today = new Date();
      const isToday =
        startDate.getFullYear() === today.getFullYear() &&
        startDate.getMonth() === today.getMonth() &&
        startDate.getDate() === today.getDate();
      updates.status = isToday ? 'live' : 'scheduled';
    }

    handleChange(updates);
  };
  const handleImageUpload = async (file: File) => {
    if (hasBrandLogo) {
      toast.info(t('campaign.brandLogoAlreadyExists'));
      return;
    }
    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        brandLogo: String(t('campaign.errors.invalidFileType')),
      }));
      setIsImageValid(false);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        brandLogo: String(t('campaign.errors.fileSizeExceeded')),
      }));
      setIsImageValid(false);
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = async () => {
      if (img.width !== img.height) {
        setErrors((prev) => ({
          ...prev,
          brandLogo: String(t('common.imageNotSquareError')),
        }));
        setIsImageValid(false);
        URL.revokeObjectURL(objectUrl);
        return;
      }

      setIsImageValid(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
        setPreviewFileName(file.name);
        setErrors((prev) => ({ ...prev, brandLogo: undefined }));
        handleChange({ brandLogo: reader.result as string });
      };
      reader.readAsDataURL(file);

      try {
        setIsLoading(true);
        const data = await uploadBrandImage(file);

        if (data?.link) {
          setFileName(data.link);
          handleChange({ brandLogo: data.link });
        }
      } catch (error) {
        toast.error(t('common.uploadFailed'));
        setIsImageValid(false);
      } finally {
        setIsLoading(false);
        URL.revokeObjectURL(objectUrl);
      }
    };

    img.onerror = () => {
      setErrors((prev) => ({
        ...prev,
        brandLogo: String(t('common.imageNotSquareError')),
      }));
      setIsImageValid(false);
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});
      onNext();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: Partial<
          Record<keyof FormDataForCreateCampaign, string>
        > = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path as keyof FormDataForCreateCampaign] =
              error.message;
          }
        });
        setErrors(newErrors);
        toast.error(String(t('campaign.errors.fixErrorsBeforeProceeding')));
      }
    }
  };

  return (
    <>
      {showActiveCampaignModal && (
        <ActiveCampaignRunningModal
          isOpen={showActiveCampaignModal}
          title="Active Campaign Running"
          message="You can create a new campaign after the current one ends."
          onClose={() => {
            setShowActiveCampaignModal(false);
            navigate('/active-campaign');
          }}
        />
      )}
      <form
        onSubmit={handleSubmit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
        className="flex flex-col flex-1 min-h-0"
      >
        <div className="flex-1 min-h-0 overflow-y-auto space-y-6 pr-1">
          <h3 className="text-base font-semibold text-[#172B4D]">
            {t(`common.campaignDetails`)}
          </h3>
          <div className="flex flex-col md:flex-row md:space-x-6">
            <div className="w-3/5 flex gap-6">
              <div className="flex flex-col">
                <DragDropComponent
                  imagePreview={imagePreview}
                  setImagePreview={setImagePreview}
                  fileName={previewFileName}
                  setFileName={setFileName}
                  onFileSelect={
                    hasBrandLogo || isEditCampaign
                      ? undefined
                      : handleImageUpload
                  }
                  error={errors.brandLogo}
                  isLoading={isLoading}
                  disabled={hasBrandLogo || isEditCampaign}
                />
              </div>
              <div className="flex-1 flex flex-col gap-4">
                <div>
                  <Input
                    type="text"
                    value={formData.brandName || ''}
                    name="brandName"
                    placeholder={String(t('profile.campaignName'))}
                    disabled
                    className="bg-[#F4F4F4] text-[#113E53] cursor-not-allowed"
                  />
                  {errors.brandName && (
                    <p className="text-sm text-red-500">{errors.brandName}</p>
                  )}
                </div>
                {/* <div className="relative">
              {!isStartFocused && !formData.startDate && (
                <div className="absolute inset-y-0 left-4 z-10 flex items-center text-[#B3B3B2] pointer-events-none">
                  {String(t('common.startDate'))}
                </div>
              )}
              <Input
                ref={startDateRef}
                id="startDate"
                name="startDate"
                type={isStartFocused ? 'datetime-local' : 'text'}
                className={!isStartFocused ? 'caret-transparent' : ''}
                value={
                  isStartFocused
                    ? formatDateTimeLocal(formData.startDate)
                    : formatDateTimeText(formData.startDate)
                }
                min={
                  isStartFocused
                    ? new Date().toISOString().slice(0, 16)
                    : undefined
                }
                onMouseDown={(e) => {
                  if (!isStartFocused) {
                    e.preventDefault();
                    setIsStartFocused(true);

                    requestAnimationFrame(() => {
                      startDateRef.current?.focus();
                      startDateRef.current?.showPicker?.();
                    });
                  }
                }}
                onBlur={() => setIsStartFocused(false)}
                onChange={(e) => {
                  if (!isStartFocused) return;
                  const v = e.target.value;
                  const iso = v ? new Date(v).toISOString() : '';
                  handleChangeWithValidation('startDate', iso);
                  if (!v) return;
                  setIsStartFocused(false);
                  requestAnimationFrame(() => {
                    startDateRef.current?.blur();
                  });
                }}
              />
              {errors.startDate && (
                <p className="text-sm text-red-500">{errors.startDate}</p>
              )}
            </div> */}
                <div>
                  <ConfigProvider locale={enUS}>
                    <DatePicker
                      showTime
                      value={
                        formData.startDate ? dayjs(formData.startDate) : null
                      }
                      placeholder={String(t('common.startDate'))}
                      format="DD-MM-YYYY hh:mm A"
                      className="
        w-full
        h-[48px]
        rounded-full
        border
        border-[#B3B3B2]
        px-4
        text-[#113E53]
        text-lg
        focus:border-[#113E53]
      "
                      popupClassName="rounded-xl"
                      disabledDate={(current) =>
                        current && current < dayjs().startOf('day')
                      }
                      onChange={(value) => {
                        handleChangeWithValidation('startDate', toISO(value));
                      }}
                    />
                  </ConfigProvider>
                  {errors.startDate && (
                    <p className="text-sm text-red-500">{errors.startDate}</p>
                  )}
                </div>
                {/* <div className="relative">
              {!isEndFocused && !formData.endDate && !isBalanceExhausted && (
                <div className="absolute inset-y-0 left-4 z-10 flex items-center text-[#B3B3B2] pointer-events-none">
                  {String(t('common.endDate'))}
                </div>
              )}
              <Input
                ref={endDateRef}
                id="endDate"
                name="endDate"
                disabled={isBalanceExhausted}
                type={isEndFocused ? 'datetime-local' : 'text'}
                className={!isEndFocused ? 'caret-transparent' : ''}
                value={
                  isEndFocused
                    ? formatDateTimeLocal(formData.endDate)
                    : formatDateTimeText(formData.endDate)
                }
                min={
                  isEndFocused
                    ? formData.startDate
                      ? formatDateTimeLocal(formData.startDate)
                      : new Date().toISOString().slice(0, 16)
                    : undefined
                }
                onMouseDown={(e) => {
                  if (isBalanceExhausted) return;

                  if (!isEndFocused) {
                    e.preventDefault();
                    setIsEndFocused(true);

                    requestAnimationFrame(() => {
                      endDateRef.current?.focus();
                      endDateRef.current?.showPicker?.();
                    });
                  }
                }}
                onBlur={() => setIsEndFocused(false)}
                onChange={(e) => {
                  if (!isEndFocused) return;

                  const v = e.target.value;
                  const iso = v ? new Date(v).toISOString() : '';
                  handleChangeWithValidation('endDate', iso);

                  if (!v) return;

                  setIsEndFocused(false);
                  requestAnimationFrame(() => {
                    endDateRef.current?.blur();
                  });
                }}
              />
              {errors.endDate && (
                <p className="text-sm text-red-500">{errors.endDate}</p>
              )}
            </div> */}
                <div>
                  <ConfigProvider locale={enUS}>
                    <DatePicker
                      showTime
                      value={
                        isBalanceExhausted
                          ? null
                          : formData.endDate
                            ? dayjs(formData.endDate, 'YYYY-MM-DDTHH:mm:ss')
                            : null
                      }
                      placeholder={String(t('common.endDate'))}
                      disabled={isBalanceExhausted}
                      format="DD-MM-YYYY hh:mm A"
                      className="
  w-full
  h-[48px]
  rounded-full
  border
  border-[#B3B3B2]
  px-4
  text-[#113E53]
  text-lg
  focus:border-[#113E53]
"
                      popupClassName="rounded-xl"
                      disabledDate={(current) => {
                        if (!current || !formData.startDate) return false;
                        const start = dayjs(formData.startDate).startOf(
                          'minute',
                        );
                        const minEndDate = start.add(1, 'month');
                        return (
                          current.startOf('minute').valueOf() <=
                          minEndDate.valueOf()
                        );
                      }}
                      onChange={(value) => {
                        handleChangeWithValidation(
                          'endDate',
                          toLocalDateTimeString(value),
                        );
                      }}
                    />
                  </ConfigProvider>

                  {errors.endDate && (
                    <p className="text-sm text-red-500">{errors.endDate}</p>
                  )}
                </div>
                <div className="flex items-center">
                  <Checkbox
                    className="!h-4 !w-4 !accent-[#113E53]"
                    label={String(t('common.tillTheBalanceGetExhausted'))}
                    labelClassName="text-sm text-gray-700 font-normal"
                    checked={isBalanceExhausted}
                    onChange={handleCheckboxChange}
                  />
                </div>
              </div>
            </div>

            <div className="w-2/5">
              <div className="relative">
                <textarea
                  ref={textareaRef}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  onClick={() => {
                    textareaRef.current?.focus();
                    setIsFocused(true);
                  }}
                  value={formData?.brandDescription || ''}
                  onChange={(e) =>
                    handleChangeWithValidation(
                      'brandDescription',
                      e.target.value,
                    )
                  }
                  rows={4}
                  aria-label={String(t('campaign.labels.brandDescription'))}
                  placeholder={
                    isFocused ? '' : String(t(`common.brandDescription`))
                  }
                  className="block w-full border border-[#B3B3B2] py-3 px-4 h-[172px] rounded-[24px] placeholder-[#B3B3B2] bg-white outline-none focus:border focus:border-[#113E53] text-[#113E53] text-base leading-[22px] resize-none whitespace-pre-wrap overflow-auto"
                  style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  }}
                />
                {!formData?.brandDescription && !isFocused && (
                  <div className="absolute right-4 top-3 pointer-events-auto">
                    <ToolTip
                      text={String(t(`common.brandDescriptionTooltip`))}
                      position="left"
                      className="tooltip-wide"
                    >
                      <InfoIcon className="w-5 h-5 cursor-pointer text-[#94A3B8]" />
                    </ToolTip>
                  </div>
                )}
                {/* {formData?.brandDescription && (
              <button
                onClick={() =>
                  handleChangeWithValidation('brandDescription', '')
                }
                className="absolute right-[10px] top-[8px] text-gray-600 hover:text-gray-800"
                aria-label={String(t('common.close'))}
                type="button"
              >
                <img
                  src="/assets/fi_close_black.svg"
                  alt={String(t('campaign.altTexts.remove'))}
                  className="h-[16px] w-[16px]"
                />
              </button>
            )} */}
                {errors.brandDescription && (
                  <p className="text-sm text-red-500">
                    {errors.brandDescription}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white flex justify-end w-full shrink-0">
          <div className="flex gap-4 w-full p-6 justify-end">
            <Button type="submit" className="w-[122px]">
              {t('common.next')}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

export default CreateCampaignStep1;
